import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const INITIAL_CONFIRM_FORM = {
  otp: '',
  password: '',
  confirmPassword: '',
};

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [contact, setContact] = useState('');
  const [confirmForm, setConfirmForm] = useState(INITIAL_CONFIRM_FORM);
  const [requestingCode, setRequestingCode] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [requested, setRequested] = useState(false);

  const requestCode = async (event) => {
    event?.preventDefault();

    if (!contact.trim()) {
      toast.error('Enter your email address or phone number');
      return;
    }

    setRequestingCode(true);
    try {
      const response = await axios.post('/api/auth/password-reset/request', {
        contact: contact.trim(),
      });
      setRequested(true);
      toast.success(response.data?.message || 'If your account exists, a reset code has been sent.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not send reset code');
    } finally {
      setRequestingCode(false);
    }
  };

  const submitReset = async (event) => {
    event.preventDefault();

    if (!contact.trim()) {
      toast.error('Enter your email address or phone number');
      return;
    }
    if (!confirmForm.otp.trim()) {
      toast.error('Enter the 6-digit reset code');
      return;
    }
    if (confirmForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (confirmForm.password !== confirmForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setResettingPassword(true);
    try {
      const response = await axios.post('/api/auth/password-reset/confirm', {
        contact: contact.trim(),
        otp: confirmForm.otp.trim(),
        password: confirmForm.password,
      });
      toast.success(response.data?.message || 'Password reset successful');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <div className="auth-intro">
          <p className="auth-kicker">Recovery access</p>
          <h1>Reset your password with a one-time code sent by email and SMS.</h1>
          <p>
            Enter the email address or phone number on your account. If we find a match, we will
            send a 6-digit code to your registered recovery channels so you can get back in fast.
          </p>
        </div>

        <div className="auth-card">
          <div className="auth-card-header">
            <div className="logo">D2D<span>SHIPPING PLATFORM</span></div>
            <p>Recover your account securely</p>
          </div>

          <form onSubmit={requested ? submitReset : requestCode}>
            <div className="input-group">
              <label>Email Address or Phone Number</label>
              <input
                className="input-field"
                type="text"
                placeholder="you@example.com or +233XXXXXXXXX"
                value={contact}
                onChange={(event) => setContact(event.target.value)}
                required
              />
            </div>

            {requested ? (
              <>
                <p className="auth-helper">
                  If the account exists, we sent a 6-digit code to the email address and phone
                  number already saved on that profile.
                </p>

                <div className="input-group">
                  <label>Reset Code</label>
                  <input
                    className="input-field auth-code-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="123456"
                    value={confirmForm.otp}
                    onChange={(event) =>
                      setConfirmForm((current) => ({
                        ...current,
                        otp: event.target.value.replace(/\D/g, '').slice(0, 6),
                      }))
                    }
                    required
                  />
                </div>

                <div className="input-group">
                  <label>New Password</label>
                  <input
                    className="input-field"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={confirmForm.password}
                    onChange={(event) =>
                      setConfirmForm((current) => ({ ...current, password: event.target.value }))
                    }
                    required
                  />
                </div>

                <div className="input-group">
                  <label>Confirm New Password</label>
                  <input
                    className="input-field"
                    type="password"
                    placeholder="Repeat your new password"
                    value={confirmForm.confirmPassword}
                    onChange={(event) =>
                      setConfirmForm((current) => ({
                        ...current,
                        confirmPassword: event.target.value,
                      }))
                    }
                    required
                  />
                </div>

                <div className="auth-actions-inline">
                  <button type="submit" className="btn btn-primary btn-full" disabled={resettingPassword}>
                    {resettingPassword ? <span className="spinner" /> : 'Reset Password'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-full"
                    onClick={requestCode}
                    disabled={requestingCode}
                  >
                    {requestingCode ? <span className="spinner spinner-dark" /> : 'Resend Code'}
                  </button>
                </div>
              </>
            ) : (
              <button type="submit" className="btn btn-primary btn-full" disabled={requestingCode}>
                {requestingCode ? <span className="spinner" /> : 'Send Reset Code'}
              </button>
            )}
          </form>

          <div className="auth-link-row">
            <p>Remembered your password? <Link to="/login">Back to sign in</Link></p>
            <p>Need an account instead? <Link to="/register">Create one</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}
